import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormikProps } from 'formik';
import { cleanObj } from '../../../../../shared/utils/objects';
import { ToastSwal } from '../../../../components/general/toastSwals/toastSwal';
import { Attachment, ISelect } from '../../../../../shared/types/shared';
import {
    Pqr,
    PqrCallInfo,
    PqrData,
    PqrDataSfc,
    PqrInfo,
    PqrInitialCausality,
    PqrNotificationClient,
    PqrProductData,
    PqrReconsideration,
    PqrStrategicAlly,
} from '../../../../../shared/types/modules/pqr';
import {
    asyncCreatePqr,
    asyncGetPqr,
    asyncUpdatePqr,
} from '../../../../../services/pqr.service';
import { PQR, PQR_DETAIL } from '../../../../../routes/paths';

export const usePqrForm = (isBpInteraction?: boolean) => {
    const navigate = useNavigate();
    const { idPqr } = useParams();
    const { idAccount } = useParams();
    const [pqr, setPqr] = useState<Pqr>();
    const [loading, setLoading] = useState(false);
    const [formikPqrCallInfo, setFormikPqrCallInfo] =
        useState<FormikProps<PqrCallInfo>>();
    const [formikPqrData, setFormikPqrData] = useState<FormikProps<PqrData>>();
    const [formikPqrDataSfc, setFormikPqrDataSfc] =
        useState<FormikProps<PqrDataSfc>>();
    const [formikPqrInitialCausality, setFormikPqrInitialCausality] =
        useState<FormikProps<PqrInitialCausality>>();
    const [formikPqrNotificationClient, setFormikPqrNotificationClient] =
        useState<FormikProps<PqrNotificationClient>>();
    const [formikPqrInfo, setFormikPqrInfo] = useState<FormikProps<PqrInfo>>();
    const [formikPqrReconsideration, setFormikPqrReconsideration] =
        useState<FormikProps<PqrReconsideration>>();
    const [formikPqrProductData, setFormikPqrProductData] =
        useState<FormikProps<PqrProductData>>();
    const [formikPqrStrategicAlly, setFormikPqrStrategicAlly] =
        useState<FormikProps<PqrStrategicAlly>>();
    const [attachments, setAttachments] = useState<Attachment[]>([]);

    const [isOpenErrorSFCModal, setIsOpenErrorSFCModal] = useState(false);
    const [responseSmart, setResponseSmart] = useState();
    const [newPqrId, setNewPqrId] = useState<string>();
    const [initialCausalityCurrent, setInitialCausalityCurrent] = useState<
        string | undefined
    >(undefined);

    const isValidForm =
        formikPqrCallInfo?.isValid &&
        formikPqrData?.isValid &&
        formikPqrDataSfc?.isValid &&
        formikPqrInitialCausality?.isValid &&
        formikPqrNotificationClient?.isValid &&
        formikPqrInfo?.isValid &&
        formikPqrReconsideration?.isValid &&
        formikPqrProductData?.isValid &&
        formikPqrStrategicAlly?.isValid;

    const handleSubmit = async () => {
        await formikPqrCallInfo?.submitForm();
        await formikPqrData?.submitForm();
        await formikPqrDataSfc?.submitForm();
        await formikPqrInitialCausality?.submitForm();
        await formikPqrNotificationClient?.submitForm();
        await formikPqrInfo?.submitForm();
        await formikPqrReconsideration?.submitForm();
        await formikPqrProductData?.submitForm();
        await formikPqrStrategicAlly?.submitForm();

        if (!isValidForm) return;

        setLoading(true);

        const data: Pqr = {};
        data.account = pqr?.account?.id || idAccount;
        data.callInfo = cleanObj(formikPqrCallInfo?.values);
        data.data = cleanObj(formikPqrData?.values);
        data.dataSfc = cleanObj(formikPqrDataSfc?.values);
        data.initialCausality = formikPqrInitialCausality?.values;
        data.initialCausalityCurrent = initialCausalityCurrent;
        data.notificationClients = cleanObj(
            formikPqrNotificationClient?.values
        );
        data.info = cleanObj(formikPqrInfo?.values);
        data.reconsideration = cleanObj(formikPqrReconsideration?.values);
        data.productData = cleanObj(formikPqrProductData?.values);
        data.strategicAlly = cleanObj(formikPqrStrategicAlly?.values);
        delete (data.strategicAlly as any).strategicAllyLabel;
        data.attachments = attachments;

        let res: Pqr | undefined = undefined;

        try {
            if (pqr && pqr.id) res = await asyncUpdatePqr(pqr.id, data);
            else res = await asyncCreatePqr(data);
        } catch (error) {}

        if (res) {
            setNewPqrId(res?.id);
            const smart = res.responseSmart?.length > 0 && res.responseSmart[0];

            if (res?.responseSmart) {
                if (smart?.result?.res?.status_code === 400) {
                    setResponseSmart(smart?.result?.res);
                    setIsOpenErrorSFCModal(true);
                }
            }
            ToastSwal.fire({
                text: `¡Felicidades! El PQR se ${
                    pqr && pqr.id ? 'actualizo' : 'creó'
                } satisfactoriamente. ${
                    res && res.consecutiveFormat
                        ? `Con consecutivo ${res.consecutiveFormat}`
                        : ''
                }`,
                icon: 'success',
                timer: 20000,
                position: 'center-end',
            });

            if (!res?.responseSmart || smart?.result?.res?.status_code !== 400)
                navigate(`${PQR_DETAIL}/${res.id}`);
        } else {
            ToastSwal.fire({
                text: `Lo sentimos, no se pudo ${
                    pqr && pqr.id ? 'actualizar' : 'crear'
                } el PQR. Por favor, asegúrate de ${
                    pqr && pqr.id
                        ? 'diligenciar los datos correctamente'
                        : 'que no exista previamente'
                } e intenta nuevamente más tarde.`,
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            });
        }

        setLoading(false);
    };

    const handleClean = () => {
        formikPqrCallInfo?.resetForm();
        formikPqrData?.resetForm();
        formikPqrDataSfc?.resetForm();
        formikPqrInitialCausality?.resetForm();
        formikPqrNotificationClient?.resetForm();
        formikPqrInfo?.resetForm();
        formikPqrReconsideration?.resetForm();
        formikPqrProductData?.resetForm();
        formikPqrStrategicAlly?.resetForm();
    };

    useEffect(() => {
        if (Boolean(idPqr?.trim())) {
            setLoading(true);
            asyncGetPqr(idPqr?.trim())
                .then((pqr) => {
                    if ((!pqr || !pqr.id) && !!isBpInteraction) navigate(PQR);

                    [
                        pqr?.data,
                        pqr?.dataSfc,
                        pqr?.initialCausality,
                        pqr?.info,
                        pqr?.reconsideration,
                    ].forEach((item: any) => {
                        if (item)
                            Object.keys(item).forEach((key) => {
                                if (item?.[key] && (item?.[key] as ISelect)?.id)
                                    item[key] = (
                                        item[key] as ISelect
                                    )?.id.toString();
                            });
                    });

                    setPqr(pqr);
                })
                .finally(() => setLoading(false));
        }
    }, [idPqr]);

    useEffect(() => {
        if (!isOpenErrorSFCModal && responseSmart) {
            navigate(`${PQR_DETAIL}/${newPqrId}`);
        }
    }, [isOpenErrorSFCModal]);

    return {
        pqr,
        idAccount,
        loading,
        isValidForm,
        handleSubmit,
        handleClean,
        formikPqrCallInfo,
        formikPqrData,
        formikPqrDataSfc,
        formikPqrInitialCausality,
        formikPqrNotificationClient,
        formikPqrInfo,
        formikPqrReconsideration,
        formikPqrProductData,
        formikPqrStrategicAlly,
        attachments,
        setFormikPqrCallInfo,
        setFormikPqrData,
        setFormikPqrDataSfc,
        setFormikPqrInitialCausality,
        setFormikPqrNotificationClient,
        setFormikPqrInfo,
        setFormikPqrReconsideration,
        setFormikPqrProductData,
        setFormikPqrStrategicAlly,
        setAttachments,
        isOpenErrorSFCModal,
        setIsOpenErrorSFCModal,
        responseSmart,
        setInitialCausalityCurrent,
    };
};
